/// <amd-module name="VSS/Features/PlatformUI/ContributedBreadcrumb" />

import { IReadonlyObservableArray, ObservableCollection } from "VSS/Core/Observable";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { traceWarning } from "VSS/Platform/Trace";

import { getContributedItems, wrapExternalObservable } from "./ContributedItemUtil";

import { IBreadcrumbItem } from "VSSUI/Breadcrumb.Types";

interface IBreadcrumbItemContributionData {
    [breadcrumbId: string]: IBreadcrumbItem[];
}

export interface IBreadcrumbItemService {
    getBreadcrumbItems: (breadcrumbId: string) => IBreadcrumbItem[] | IReadonlyObservableArray<IBreadcrumbItem> | null;
}

interface IExternalBreadcrumbItemService {
    getBreadcrumbItems: (breadcrumbId: string) => Promise<IBreadcrumbItem[] | IReadonlyObservableArray<IBreadcrumbItem> | null>;
}

/**
 * Gets an observable array of breadcrumb items for the specified breadcrumb
 *
 * @param pageContext Page context
 * @param breadcrumbId Contribution id of the breadcrumb
 */
export function getBreadcrumbItems<T>(pageContext: IVssPageContext, breadcrumbId: string): IReadonlyObservableArray<IBreadcrumbItem> {
    const itemCollection = new ObservableCollection<IBreadcrumbItem>();
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    // Determine whether or not there are any breadcrumb-items contributed through shared data.
    const breadcrumbData: IBreadcrumbItemContributionData | undefined = contributionService.getSharedData<IBreadcrumbItemContributionData>(
        "_breadcrumbItems"
    );
    if (breadcrumbData) {
        const items = breadcrumbData[breadcrumbId];
        if (items) {
            itemCollection.push(items);
        }
    }

    // Load static tab contributions
    const staticContributions = getContributedItems<IBreadcrumbItem>(pageContext, breadcrumbId, "breadcrumbId", "ms.vss-web.breadcrumb-item");
    itemCollection.push(staticContributions, c => {
        return {
            hidden: c.contribution.hidden,
            href: c.contribution.href,
            key: c.contribution.key || c.id,
            text: c.contribution.text,
            rank: c.contribution.rank,
            priority: c.contribution.priority
        };
    });

    const dynamicProviders = getContributedItems(pageContext, breadcrumbId, "breadcrumbId", "ms.vss-web.breadcrumb-provider");
    for (const dynamicProvider of dynamicProviders) {
        const breadcrumbService = contributionService.getServiceSync<IBreadcrumbItemService>(dynamicProvider.id);
        if (breadcrumbService) {
            const breadcrumbItems = breadcrumbService.getBreadcrumbItems(breadcrumbId);
            if (breadcrumbItems) {
                itemCollection.push(breadcrumbItems);
            }
        } else {
            contributionService.getService<IExternalBreadcrumbItemService | IBreadcrumbItemService>(dynamicProvider.id).then(breadcrumbService => {
                if (breadcrumbService) {
                    // For back-compat with older (extensions) which returned items directly
                    if (!breadcrumbService.getBreadcrumbItems) {
                        itemCollection.push(
                            wrapExternalObservable((breadcrumbService as any) as IBreadcrumbItem[] | IReadonlyObservableArray<IBreadcrumbItem>)
                        );
                    } else {
                        const breadcrumbItems = breadcrumbService.getBreadcrumbItems(breadcrumbId);
                        const externalBreadcrumbItems = breadcrumbItems as Promise<
                            IBreadcrumbItem[] | IReadonlyObservableArray<IBreadcrumbItem> | null
                        >;
                        if (externalBreadcrumbItems && typeof externalBreadcrumbItems.then === "function") {
                            externalBreadcrumbItems.then(
                                resolvedItems => {
                                    if (resolvedItems) {
                                        itemCollection.push(wrapExternalObservable(resolvedItems));
                                    }
                                },
                                (e: Error) => {
                                    const message = `Failed to load tabs for ${breadcrumbId}: ${JSON.stringify(e || "")}`;
                                    traceWarning(pageContext, "extensions", "ContributedBreadcrumb", message, { breadcrumbId });
                                    console.warn(message);
                                }
                            );
                        } else if (breadcrumbItems) {
                            itemCollection.push(breadcrumbItems as IBreadcrumbItem[] | IReadonlyObservableArray<IBreadcrumbItem>);
                        }
                    }
                }
            });
        }
    }

    return itemCollection;
}
